import {
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Stack,
  alpha,
} from '@mui/material';
import { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';

const CONCEPT_GROUP_FILE_UPLOAD_MAX_SIZE = 4 * 1024 * 1024;

interface FileUploadProps {
  selectedFile: File | null;
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
  /** Optional affordance next to "Change file" that clears the picked file
   *  (OR-6288: reverting to the draft's current linked list). */
  onClear?: () => void;
  clearLabel?: string;
  /** The draft's CURRENT linked file, shown in the same row style when no new
   *  file is picked (OR-6288). ``processing`` renders a spinner on the icon
   *  (lazy server-side generation of the download). */
  linkedFile?: {
    name: string;
    onClick?: () => void;
    processing?: boolean;
  } | null;
}

export default function ConceptsFileUpload({
  selectedFile,
  onFileSelect,
  isLoading,
  onClear,
  clearLabel,
  linkedFile,
}: FileUploadProps) {
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const intl = useIntl();

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setFileUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [selectedFile]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFileError(null);

      if (acceptedFiles.length === 0) {
        return;
      }

      const file = acceptedFiles[0];

      // Check file size (4MB max)
      if (file.size > CONCEPT_GROUP_FILE_UPLOAD_MAX_SIZE) {
        setFileError(intl.formatMessage({ id: 'conceptGroup.fileUpload.errorSize' }));
        return;
      }

      // Check file extension
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(extension || '')) {
        setFileError(intl.formatMessage({ id: 'conceptGroup.fileUpload.errorFormat' }));
        return;
      }

      onFileSelect(file);
    },
    [onFileSelect, intl]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    noClick: isLoading,
  });

  const renderFileInput = () => <input {...(getInputProps ? getInputProps() : {})} />;

  return (
    <Stack direction={'column'} gap={'24px'}>
      <Paper
        {...getRootProps()}
        variant="outlined"
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 1,
          borderStyle: 'dashed',
          borderWidth: 1,
          borderColor: '#666666',
          bgcolor: isDragActive ? alpha('#ccf0f9', 0.6) : '',
          cursor: 'pointer',
          opacity: isLoading ? 0.38 : 1,
          '&:hover': {
            bgcolor: !isLoading ? alpha('#ccf0f9', 0.6) : '',
          },
        }}
      >
        {renderFileInput()}
        <Icon
          id={`conceptsFileUpload-selectedFile-${selectedFile?.name}`}
          icon="upload"
          width={32}
          height={32}
          sx={{ mb: 2, color: theme.palette.primary.main }}
        />
        <Stack direction={'column'} gap={2} textAlign={'center'}>
          <Box>
            <Typography variant="h6" color={theme.palette.text.primary} fontSize={'16px'}>
              {intl.formatMessage({ id: 'conceptGroup.fileUpload.dragDrop' })}
            </Typography>
            <Typography color={theme.palette.text.secondary} fontSize={'16px'}>
              {intl.formatMessage({ id: 'conceptGroup.fileUpload.orClick' })}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color={theme.palette.text.secondary}
            fontSize={'12px'}
            whiteSpace={'pre'}
          >
            {intl.formatMessage({ id: 'conceptGroup.fileUpload.maxSize' })}
          </Typography>
        </Stack>
      </Paper>
      {selectedFile ? (
        <Paper
          variant="outlined"
          sx={{
            py: '10px',
            px: '16px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            bgcolor: 'grey.50',
          }}
        >
          <Icon
            id={`conceptsFileUpload-selectedFile-${selectedFile?.name}`}
            icon="file"
            width={24}
            height={24}
            sx={{ mr: 2, opacity: isLoading ? 0.38 : 1 }}
          />
          <Box
            component="a"
            href={fileUrl || '#'}
            download={selectedFile?.name}
            sx={{
              textDecoration: 'underline',
              textDecorationColor: '#000000',
              opacity: isLoading ? 0.38 : 1,
            }}
          >
            <Typography color={theme.palette.text.primary} fontWeight={700} fontSize={'14px'}>
              {selectedFile?.name}
            </Typography>
          </Box>
          <Typography
            sx={{
              ml: 'auto',
              color: 'primary.main',
              cursor: 'pointer',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              opacity: isLoading ? 0.38 : 1,
            }}
            onClick={!isLoading ? open : undefined}
          >
            Change file
          </Typography>
          {onClear ? (
            <Typography
              sx={{
                ml: 2,
                color: 'primary.main',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '14px',
                opacity: isLoading ? 0.38 : 1,
              }}
              onClick={!isLoading ? onClear : undefined}
              data-testid="conceptsFileUpload-clear"
            >
              {clearLabel ?? 'Remove'}
            </Typography>
          ) : null}
        </Paper>
      ) : linkedFile ? (
        <Paper
          variant="outlined"
          sx={{
            py: '10px',
            px: '16px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            bgcolor: 'grey.50',
          }}
          data-testid="conceptsFileUpload-linked-file"
        >
          {linkedFile.processing ? (
            <CircularProgress size={20} sx={{ mr: 2 }} />
          ) : (
            <Icon
              id={`conceptsFileUpload-linkedFile-${linkedFile.name}`}
              icon="file"
              width={24}
              height={24}
              sx={{ mr: 2 }}
            />
          )}
          <Box
            component={linkedFile.onClick ? 'button' : 'span'}
            type={linkedFile.onClick ? 'button' : undefined}
            onClick={linkedFile.onClick}
            sx={{
              background: 'none',
              border: 0,
              padding: 0,
              cursor: linkedFile.onClick ? 'pointer' : 'default',
              textDecoration: linkedFile.onClick ? 'underline' : 'none',
              textDecorationColor: '#000000',
            }}
          >
            <Typography color={theme.palette.text.primary} fontWeight={700} fontSize={'14px'}>
              {linkedFile.name}
            </Typography>
          </Box>
        </Paper>
      ) : null}
      {fileError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {fileError}
        </Alert>
      )}
    </Stack>
  );
}
